/**
 * One day-parting slot, mirroring the backend `AdScheduleSlot`. `day` is a
 * capitalised English weekday; `endHour` may be 24 (end of day).
 */
export interface AdScheduleSlot {
  day: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
}

/**
 * Demographic targeting, as the *included* brackets per dimension (canonical
 * labels; empty/absent means all brackets serve). Mirrors the backend
 * CampaignDemographics.
 */
export interface CampaignDemographics {
  ageRanges?: string[];
  genders?: string[];
  householdIncomes?: string[];
}

/** Canonical, display-order bracket labels for the demographics editor. */
export const AGE_LABELS = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+', 'Unknown'] as const;
export const GENDER_LABELS = ['Female', 'Male', 'Unknown'] as const;
export const INCOME_LABELS = [
  'Top 10%',
  '11-20%',
  '21-30%',
  '31-40%',
  '41-50%',
  'Lower 50%',
  'Unknown',
] as const;

/** Render one slot as "Monday 7:00 AM – 2:00 PM" (or "Monday (all day)"). */
export function describeAdScheduleSlot(slot: AdScheduleSlot): string {
  const clock = (hour: number, minute: number): string => {
    if (hour === 24 && minute === 0) return 'midnight';
    const mer = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${h12}:${String(minute).padStart(2, '0')} ${mer}`;
  };
  if (slot.startHour === 0 && slot.startMinute === 0 && slot.endHour === 24 && slot.endMinute === 0)
    return `${slot.day} (all day)`;
  return `${slot.day} ${clock(slot.startHour, slot.startMinute)} – ${clock(slot.endHour, slot.endMinute)}`;
}

export interface GoogleSearchData {
  campaignName: string;
  finalUrl: string;
  bidStrategy: string;
  dailyBudget: number;
  headlines: string[];
  descriptions: string[];
  displayPaths: [string, string];
  sitelinks: { text: string; description1: string; description2: string; finalUrl: string }[];
  callouts: string[];
  keywords: {
    keyword: string;
    matchType: 'exact' | 'phrase' | 'broad';
    resourceName?: string;
    primaryStatus?: string;
    primaryStatusReasons?: string[];
  }[];
  negativeKeywords: string[];
  zipCodes: string[];
  languages?: string[];
  // Optional campaign-level delivery/targeting settings. Ingested from an
  // ad-config import so the SaaS view reflects what will push to Google; unset
  // for AI-generated campaigns (which use Google's defaults on launch).
  status?: 'ENABLED' | 'PAUSED';
  searchPartners?: boolean;
  displayNetwork?: boolean;
  locationTargeting?: 'PRESENCE' | 'PRESENCE_OR_INTEREST';
  /** Free-text audience summary from an ad-config import, shown in the Setup →
   *  Overview. Display-only; not sent to Google on launch. */
  audienceSummary?: string;
  /** Day-parting from an ad-config import. Shown in the Setup → Overview and
   *  applied to the Google campaign as AD_SCHEDULE criteria on launch. */
  adSchedule?: AdScheduleSlot[];
  /** AI Max for Search on/off, from an ad-config import. Shown in the Setup →
   *  Overview and set on the campaign's ai_max_setting on launch. */
  aiMax?: boolean;
  /** AI Max asset automation — text customization (auto-written ad copy). */
  aiMaxTextCustomization?: boolean;
  /** AI Max asset automation — final URL expansion (relevant landing pages). */
  aiMaxFinalUrlExpansion?: boolean;
  /** Demographic targeting, as the included brackets per dimension. Editable in
   *  the Setup → Google form; applied on launch as ad-group exclusions. */
  demographics?: CampaignDemographics;
  /** Pin position per headline (parallel to `headlines`), from an import. */
  headlinePins?: ('HEADLINE_1' | 'HEADLINE_2' | 'HEADLINE_3' | null)[];
  /** Phone numbers attached as call assets ("Calls from ads"). */
  callAssets?: string[];
}

export interface GooglePMaxData {
  campaignName: string;
  finalUrl: string;
  bidStrategy: string;
  dailyBudget: number;
  assetGroupName: string;
  businessName: string;
  headlines: string[];
  longHeadlines: string[];
  descriptions: string[];
  sitelinks: { text: string; description1: string; description2: string; finalUrl: string }[];
  callouts: string[];
  audienceSignals: { customAudiences: string[]; interests: string[]; demographics: string };
  imageSuggestions: { slot: string; description: string; aspectRatio: string }[];
}

export interface MetaAd {
  name: string;
  format: 'single_image' | 'carousel' | 'video';
  // 1–5 variants, each max 125 chars. Meta rotates via asset_feed_spec.bodies.
  // Legacy rows may store a single string; normalize at the read boundary.
  primaryText: string[];
  headline: string; // max 40 chars
  description: string; // max 30 chars
  cta: string;
  /**
   * Brief for the human uploading this ad's image. The actual image is
   * attached as a CampaignAsset with role `ad_<index>`.
   */
  imageSuggestion?: { description: string; aspectRatio: string };
}

export interface MetaData {
  campaignName: string;
  objective: string;
  adSetName: string;
  targeting: {
    ageMin: number;
    ageMax: number;
    genders: string[];
  };
  placements: string[];
  dailyBudget: number;
  ads: MetaAd[];
  /** Ad-set day-parting from an ad-config import. Shown in the Setup → Overview;
   *  Meta only enforces it on ad sets with a lifetime budget. */
  adSchedule?: AdScheduleSlot[];
}
